import { isNaN } from 'lodash';
import { DateTime, Duration } from 'luxon';
import { Granularity } from '../../../../reports/enums/reports';
import { TestGraphParseData, TestGraphResponse } from '../../../types';

export const getPercentageValue = (remainingValue: number, total: number) => {
  const percentage = (remainingValue / total) * 100;

  if (isNaN(percentage)) {
    return 0;
  }

  return percentage;
};

export const graphLegends = ['Inbox', 'Spam', 'Other', 'Undelivered'];

export const graphColorsArray = ['#34D399', '#FF623F', '#FFA800', '#D1D5DB'];

export const timeFrameOptions = [
  {
    label: 'Daily',
    value: Granularity.Daily,
    applicable: (duration: Duration) =>
      duration?.days >= 0 && duration?.days < 180,
  },
  {
    label: 'Weekly',
    value: Granularity.Weekly,
    applicable: (duration: Duration) => duration?.days > 7,
  },
  {
    label: 'Monthly',
    value: Granularity.Monthly,
    applicable: (duration: Duration) => duration?.days > 30,
  },
  {
    label: 'Quarterly',
    value: Granularity.Quarterly,
    applicable: (duration: Duration) => duration?.days > 90,
  },
];

export const transformGraphDataResponse = (data: TestGraphResponse): TestGraphParseData => {
  if (!data) {
    return null;
  }

  const parseData = {
    dates: data?.map((item) => item.date),
    inbox: data?.map((item) => item.inbox?.value || 0),
    spam: data?.map((item) => item.spam?.value || 0),
    others: data?.map((item) => item.others?.value || 0),
    notDelivered: data?.map((item) => item.notDelivered?.value || 0),
    sentEmails: data?.map((item) => item.sentEmails || 0),
  };

  return parseData;
};

export const aggregateData = (data, interval: Granularity) => {
  const initialData = {
    dates: [],
    inbox: [],
    spam: [],
    others: [],
    notDelivered: [],
    sentEmails: [],
  };

  const initialPeriodData = {
    inbox: 0,
    spam: 0,
    others: 0,
    notDelivered: 0,
    sentEmails: 0,
  };

  if (!data) {
    return null;
  }

  if (interval === Granularity.Daily) {
    return data;
  }

  const aggregated: any = initialData;
  let currentPeriod = null;
  let periodData: any = initialPeriodData;
  
  data.dates.forEach((date, index) => {
    const currentDate = DateTime.fromJSDate(new Date(date));

    const transformationMap = {
      [Granularity.Weekly]: (currDate) => currDate.startOf('week'),
      [Granularity.Monthly]: (currDate) => currDate.startOf('month'),
      [Granularity.Quarterly]: (currDate) => currDate.startOf('quarter'),
    };
    
    const newPeriod = transformationMap[interval](currentDate).toString();

    if (newPeriod !== currentPeriod) {
      if (currentPeriod) {
        aggregated.dates.push(currentPeriod);
        aggregated.inbox.push(periodData.inbox);
        aggregated.spam.push(periodData.spam);
        aggregated.others.push(periodData.others);
        aggregated.notDelivered.push(periodData.notDelivered);
        aggregated.sentEmails.push(periodData.sentEmails);
      }
      currentPeriod = newPeriod;
      periodData = { ...initialPeriodData };
    }

    periodData.inbox += data?.inbox?.[index] || 0;
    periodData.spam += data?.spam?.[index] || 0;
    periodData.others += data?.others?.[index] || 0;
    periodData.notDelivered += data?.notDelivered?.[index] || 0;
    periodData.sentEmails += data?.sentEmails?.[index] || 0;
  });

  if (currentPeriod) {
    aggregated.dates.push(currentPeriod);
    aggregated.inbox.push(periodData.inbox);
    aggregated.spam.push(periodData.spam);
    aggregated.others.push(periodData.others);
    aggregated.notDelivered.push(periodData.notDelivered);
    aggregated.sentEmails.push(periodData.sentEmails);
  }

  return aggregated;
};

export const getGraphSeries = (data) => [
  {
    name: 'Inbox',
    data: data?.inbox || [],
    yAxisIndex: 0,
  },
  {
    name: 'Spam',
    data: data?.spam || [],
    yAxisIndex: 0,
  },
  {
    name: 'Other',
    data: data?.others || [],
    yAxisIndex: 0,
  },
  {
    name: 'Undelivered',
    data: data?.notDelivered || [],
    yAxisIndex: 0,
    hidden: true,
  },
];
